import { Moment } from 'moment';
import {
  MustHaveIds,
  OptionalIds,
} from 'shared/components/EstimatedBudgetsTable/types';
import { Countries } from 'shared/enums';
import { AnyFunc, Row, Touchpoints } from 'shared/types';
import { getRowIdsByPriority } from 'shared/utils';
import { create } from 'zustand';

export interface ParametersFormData {
  budget?: number;
  exchangeRate?: number;
  divisionName: string;
  campaignTypeName: string;
  campaignStartDate: Moment | object | string;
  youtubeTargetGroup: string;
  metaTargetGroup: string;
  tiktokTargetGroup: string;
  campaignBumper: number;
  campaignDuration: number | null;
  campaignEndDate: Moment | object | string;
  touchpoints: Touchpoints[];
}

export interface CountryData {
  formData: ParametersFormData;
  estimatedBudget: Row[];
  parameters: Row[];
  optionalIds: OptionalIds;
  mustHaveIds: MustHaveIds;
  reset: () => void;
  updateForm: (newForm?: ParametersFormData) => void;
  updateEstimatedBudget: (estimatedBudget: Row[]) => void;
  updateParameters: (parameters: Row[]) => void;
  updateOptionalIds: (newOptionalIds: OptionalIds) => void;
  updateMustHaveIds: (newMustHaveIds: MustHaveIds) => void;
}

export interface StoreCountriesData {
  [Countries.CZECHIA]: CountryData;
  [Countries.SLOVAKIA]: CountryData;
  [Countries.HUNGARY]: CountryData;
  [Countries.LITHUANIA]: CountryData;
  [Countries.LATVIA]: CountryData;
  [Countries.ESTONIA]: CountryData;
}

const INITIAL_DATA = {
  estimatedBudget: [],
  parameters: [],
  optionalIds: getRowIdsByPriority([], false),
  mustHaveIds: getRowIdsByPriority([], true),
  formData: {} as ParametersFormData,
};

const getInitialState = (country: Countries, set: AnyFunc) => {
  return {
    ...INITIAL_DATA,
    reset: () => {
      set((state: StoreCountriesData) => ({
        ...state,
        [country]: {
          ...state[country],
          ...INITIAL_DATA,
        },
      }));
    },
    updateForm: (newForm?: ParametersFormData) =>
      set((state: StoreCountriesData) => ({
        ...state,
        [country]: {
          ...state[country],
          formData: newForm || ({} as ParametersFormData),
        },
      })),
    updateEstimatedBudget: (estimatedBudget: Row[]) => {
      set((state: StoreCountriesData) => ({
        ...state,
        [country]: {
          ...state[country],
          estimatedBudget,
        },
      }));
    },
    updateParameters: (parameters: Row[]) => {
      set((state: StoreCountriesData) => ({
        ...state,
        [country]: {
          ...state[country],
          parameters,
        },
      }));
    },
    updateOptionalIds: (optionalIds: OptionalIds) => {
      set((state: StoreCountriesData) => ({
        ...state,
        [country]: {
          ...state[country],
          optionalIds,
        },
      }));
    },
    updateMustHaveIds: (mustHaveIds: MustHaveIds) => {
      set((state: StoreCountriesData) => ({
        ...state,
        [country]: {
          ...state[country],
          mustHaveIds,
        },
      }));
    },
  };
};

export const useCountriesDataStore = create<StoreCountriesData>((set) => ({
  [Countries.CZECHIA]: getInitialState(Countries.CZECHIA, set),
  [Countries.SLOVAKIA]: getInitialState(Countries.SLOVAKIA, set),
  [Countries.HUNGARY]: getInitialState(Countries.HUNGARY, set),
  [Countries.LITHUANIA]: getInitialState(Countries.LITHUANIA, set),
  [Countries.LATVIA]: getInitialState(Countries.LATVIA, set),
  [Countries.ESTONIA]: getInitialState(Countries.ESTONIA, set),
}));
